import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicSurvey, useSurveyPage, useSubmitSurvey } from '../../hooks/surveys/usePublicSurvey';
import { useEventContext } from '../../contexts/EventContext';
import { useSurveyDraft } from '../../hooks/surveys/useSurveyDraft';
import { useSurveyValidation } from '../../hooks/surveys/useSurveyValidation';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { SurveyProgressBar } from './SurveyProgressBar';
import { SurveyNavigation } from './SurveyNavigation';
import { SurveyPage } from './SurveyPage';
import { SurveyConfirmation } from './SurveyConfirmation';
import { SurveyWelcome } from './SurveyWelcome';
import { SurveyContext } from './SurveyContext';
import { AuthenticationRequired } from '@portal/components/common/AuthenticationRequired';
import { StructuredApiError } from '@portal/api/apiErrors';
import type { SurveyState, Question, SurveyResponses } from '../../types/surveys/publicSurvey.types';

export const PublicSurvey: React.FC = () => {
  const { surveySlug } = useParams<{ surveySlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { eventSlug, event } = useEventContext();
  const { currentUser } = useCurrentUser();

  // Fetch survey data
  const { data: survey, isLoading: isSurveyLoading, error: surveyError } = usePublicSurvey(
    eventSlug || '',
    surveySlug || ''
  );

  // Survey state
  const [surveyState, setSurveyState] = useState<SurveyState>({
    currentPageIndex: 0,
    surveyData: {},
    touchedFields: new Set(),
    visibleChildQuestions: new Set(),
    validationErrors: {},
    isSubmitting: false,
    showConfirmation: false,
  });

  // Welcome page state
  const shouldSkipWelcome = location.pathname.endsWith('/new');
  const [showWelcomePage, setShowWelcomePage] = useState(!shouldSkipWelcome);
  const [hasStartedSurvey, setHasStartedSurvey] = useState(shouldSkipWelcome);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication requirements
  useEffect(() => {
    if (survey) {
      setRequiresAuth(survey.requireAccount || false);
      setIsAuthenticated(!!currentUser);
    }
  }, [survey, currentUser]);

  // Draft management
  const {
    draft,
    isDraftLoading,
    isSaving,
    hasPendingChanges,
    lastSaved,
    saveError,
    saveChanges,
    saveNow,
    clearDraft
  } = useSurveyDraft(hasStartedSurvey ? (eventSlug || '') : '', hasStartedSurvey ? (surveySlug || '') : '');

  // Page validation
  const { validatePage } = useSurveyValidation(surveyState);

  // Fetch current page data
  const { data: pageData, isLoading: isPageLoading } = useSurveyPage(
    eventSlug || '',
    surveySlug || '',
    surveyState.currentPageIndex,
    hasStartedSurvey
  );

  // Load draft data on mount
  useEffect(() => {
    if (draft && !isDraftLoading && Object.keys(surveyState.surveyData).length === 0) {
      setSurveyState(prev => ({
        ...prev,
        surveyData: draft.responses || {},
        currentPageIndex: draft.currentPageIndex || 0,
        draft: draft,
      }));
    }
  }, [draft, isDraftLoading]);

  // Form submission mutation
  const submitMutation = useSubmitSurvey();

  // Use colors from event style
  const primaryColor = event?.style?.headerColor || '#3B82F6';

  // Survey actions
  const updateField = useCallback((questionId: string, value: any) => {
    setSurveyState(prev => ({
      ...prev,
      surveyData: {
        ...prev.surveyData,
        [questionId]: value,
      },
    }));

    // Autosave changes
    if (draft && survey) {
      const updates = {
        responses: {
          [questionId]: value
        }
      };
      saveChanges(updates);
    }
  }, [draft, survey, saveChanges]);

  const markFieldTouched = useCallback((questionId: string) => {
    setSurveyState(prev => ({
      ...prev,
      touchedFields: new Set(prev.touchedFields).add(questionId),
    }));
  }, []);

  const setVisibleChildQuestions = useCallback((visibleSet: Set<string>) => {
    setSurveyState(prev => ({
      ...prev,
      visibleChildQuestions: visibleSet,
    }));
  }, []);

  const nextPage = useCallback(async () => {
    if (!pageData?.page) return;

    // Get all questions on current page
    const currentPageQuestions: Question[] = [];
    pageData.page.surveySections.forEach(section => {
      currentPageQuestions.push(...section.questions);
    });

    // Mark all fields as touched
    const newTouchedFields = new Set(surveyState.touchedFields);
    currentPageQuestions.forEach(q => {
      newTouchedFields.add(q.id);
    });

    setSurveyState(prev => ({
      ...prev,
      touchedFields: newTouchedFields,
    }));

    // Validate current page
    const validation = validatePage(currentPageQuestions);

    if (!validation.isValid) {
      setSurveyState(prev => ({
        ...prev,
        validationErrors: validation.errors,
      }));

      // Scroll to first error
      const firstErrorId = Object.keys(validation.errors)[0];
      const errorElement = document.getElementById(`question-${firstErrorId}`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    // Save any pending changes and current page index
    if (draft) {
      await saveNow();
      // Update page index after ensuring all changes are saved
      saveChanges({ currentPageIndex: surveyState.currentPageIndex + 1 });
    }

    // Clear errors and proceed to next page
    if (pageData?.survey?.totalPages && surveyState.currentPageIndex < pageData.survey.totalPages - 1) {
      window.scrollTo(0, 0);
      setSurveyState(prev => ({
        ...prev,
        currentPageIndex: prev.currentPageIndex + 1,
        validationErrors: {},
      }));
    }
  }, [pageData, surveyState.currentPageIndex, surveyState.touchedFields, validatePage, draft, saveNow, saveChanges]);

  const previousPage = useCallback(() => {
    if (surveyState.currentPageIndex > 0) {
      window.scrollTo(0, 0);
      setSurveyState(prev => ({
        ...prev,
        currentPageIndex: prev.currentPageIndex - 1,
      }));

      // Save page index
      if (draft) {
        saveChanges({ currentPageIndex: surveyState.currentPageIndex - 1 });
      }
    }
  }, [surveyState.currentPageIndex, draft, saveChanges]);

  const submitSurvey = useCallback(async () => {
    if (!survey || !draft) return;

    // Validate all pages before submission
    let hasErrors = false;
    const allErrors: Record<string, string> = {};

    // Validate current page
    if (pageData?.page) {
      const currentPageQuestions: Question[] = [];
      pageData.page.surveySections.forEach(section => {
        currentPageQuestions.push(...section.questions);
      });

      const validation = validatePage(currentPageQuestions);
      if (!validation.isValid) {
        hasErrors = true;
        Object.assign(allErrors, validation.errors);
      }
    }

    if (hasErrors) {
      setSurveyState(prev => ({
        ...prev,
        validationErrors: allErrors,
      }));
      return;
    }

    setSurveyState(prev => ({ ...prev, isSubmitting: true }));

    try {
      // Save any pending changes and wait for completion
      await saveNow();

      // Double-check that all changes are saved
      if (hasPendingChanges || isSaving) {
        // Wait a bit more to ensure backend has processed the save
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const submissionData: SurveyResponses = {
        authToken: draft.authToken,
        responses: surveyState.surveyData
      };

      const result = await submitMutation.mutateAsync({
        eventSlug: eventSlug || '',
        surveySlug: surveySlug || '',
        responses: submissionData,
      });

      // Clear draft after successful submission
      clearDraft();

      setSurveyState(prev => ({
        ...prev,
        isSubmitting: false,
        showConfirmation: true,
        confirmationData: result,
      }));

    } catch (error) {
      console.error('Survey submission error:', error);
      setSurveyState(prev => ({
        ...prev,
        isSubmitting: false,
        validationErrors: error instanceof Error && 'errors' in error
          ? (error as any).errors
          : { base: 'An error occurred submitting the survey. Please try again.' }
      }));
    }
  }, [survey, surveyState.surveyData, submitMutation, draft, saveNow, clearDraft, eventSlug, surveySlug, pageData, validatePage]);

  const handleBackToEvent = () => {
    if (hasStartedSurvey && Object.keys(surveyState.surveyData).length > 0 && !surveyState.showConfirmation) {
      setShowExitConfirmation(true);
    } else {
      navigate('/details');
    }
  };

  const confirmExit = () => {
    if (surveySlug) {
      clearDraft();
    }
    navigate('/details');
  };

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    if (!hasStartedSurvey) {
      setHasStartedSurvey(true);
      setShowWelcomePage(false);
    }
  };

  // Loading state
  if (isSurveyLoading || (survey && hasStartedSurvey && isDraftLoading)) {
    return (
      <div className="flex flex-col items-center justify-center p-32">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">
            {isSurveyLoading ? 'Loading survey...' : 'Preparing your session...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (surveyError || !survey) {
    // Check error reason from JSON:API format
    let errorIcon = <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />;
    let errorReason = null;
    let errorTitle = 'Survey Not Found';
    let errorMessage = "The survey you're looking for doesn't exist or is no longer available.";

    if (surveyError instanceof StructuredApiError && surveyError.originalData) {
      // Check for meta.reason in JSON:API error format
      if (surveyError.originalData.errors && Array.isArray(surveyError.originalData.errors)) {
        const firstError = surveyError.originalData.errors[0];
        errorReason = firstError?.meta?.reason;
      }
      // Fallback to check direct reason property
      else if (surveyError.originalData.reason) {
        errorReason = surveyError.originalData.reason;
      }
    }

    // Set appropriate title and message based on error reason
    switch (errorReason) {
      case 'already_completed':
        errorIcon = <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />;
        errorTitle = 'Survey Already Completed';
        errorMessage = 'You have already completed this survey. Thank you for your response!';
        break;
      case 'requires_authentication':
        errorTitle = 'Authentication Required';
        errorMessage = 'Please sign in to take this survey.';
        break;
      case 'not_yet_open':
        errorTitle = 'Survey Not Yet Open';
        errorMessage = 'This survey is not yet available. Please check back later.';
        break;
      case 'closed':
        errorTitle = 'Survey Closed';
        errorMessage = 'This survey has closed and is no longer accepting responses.';
        break;
      case 'archived':
        errorTitle = 'Survey No Longer Available';
        errorMessage = 'This survey has been archived and is no longer accepting responses.';
        break;
    }

    return (
      <div className="flex flex-col p-32">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            {errorIcon}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{errorTitle}</h2>
            <p className="text-gray-600 mb-4">
              {errorMessage}
            </p>
            <button
              onClick={handleBackToEvent}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Event
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check survey availability
  if (!survey.canBeTaken) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Survey Not Available</h2>
            <p className="text-gray-600 mb-4">
              {survey.hasAlreadyResponded && 'You have already completed this survey.'}
              {!survey.hasAlreadyResponded && survey.startsAt && new Date(survey.startsAt) > new Date() &&
                'This survey is not yet open for responses.'}
              {!survey.hasAlreadyResponded && survey.endsAt && new Date(survey.endsAt) < new Date() &&
                'This survey is no longer accepting responses.'}
              {!survey.hasAlreadyResponded && survey.state === 'archived' &&
                'This survey has been archived.'}
            </p>
            <button
              onClick={handleBackToEvent}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Event
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show confirmation screen
  if (surveyState.showConfirmation) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1">
          <SurveyConfirmation
            message="Thank you for completing the survey!"
            confirmationData={surveyState.confirmationData}
            primaryColor={primaryColor}
            onBackToEvent={handleBackToEvent}
          />
        </div>
      </div>
    );
  }

  // Show welcome page if not skipped and not started
  if (showWelcomePage && !hasStartedSurvey) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1">
          <SurveyWelcome
            survey={survey}
            primaryColor={primaryColor}
            onStart={() => {
              if (requiresAuth && !isAuthenticated) {
                setShowWelcomePage(false);
                return;
              }

              setHasStartedSurvey(true);
              setShowWelcomePage(false);
            }}
          />
        </div>
      </div>
    );
  }

  // Show authentication required if needed
  if (requiresAuth && !isAuthenticated && !showWelcomePage) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <AuthenticationRequired
              context="survey"
              primaryColor={primaryColor}
              onAuthenticated={handleAuthenticated}
            />
          </div>
        </div>
      </div>
    );
  }

  const surveyActions = {
    updateField,
    markFieldTouched,
    setVisibleChildQuestions,
    nextPage,
    previousPage,
    submitSurvey,
  };

  return (
    <SurveyContext.Provider value={{ state: surveyState, actions: surveyActions }}>
      <div className="min-h-full">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={handleBackToEvent}
                className="flex items-center text-gray-600 hover:text-gray-900 bg-white px-3 py-1.5 rounded-lg shadow-sm mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Exit Survey
              </button>

              {/* Survey header */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{survey.name}</h1>
                  {/* Autosave status */}
                  {draft && (
                    <div className="text-sm text-gray-500">
                      {isSaving ? (
                        <span className="flex items-center">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Saving...
                        </span>
                      ) : lastSaved ? (
                        <span className="text-green-600">
                          ✓ Saved
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                {survey.description && (
                  <div
                    className="prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: survey.description }}
                  />
                )}

                {/* Progress Bar */}
                {pageData?.survey?.totalPages && pageData.survey.totalPages > 1 && (
                  <SurveyProgressBar
                    current={surveyState.currentPageIndex}
                    total={pageData.survey.totalPages}
                    primaryColor={primaryColor}
                  />
                )}
              </div>
            </div>

            {/* Validation Errors Summary */}
            {Object.keys(surveyState.validationErrors).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Please correct the following errors:</h3>
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                      {Object.entries(surveyState.validationErrors).map(([fieldId, error]) => (
                        <li key={fieldId}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Survey Content */}
            {isPageLoading ? (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading page...</p>
              </div>
            ) : pageData?.page ? (
              <>
                <SurveyPage
                  page={pageData.page}
                  primaryColor={primaryColor}
                />
                <SurveyNavigation
                  currentPage={surveyState.currentPageIndex}
                  totalPages={pageData.survey?.totalPages || 1}
                  isLastPage={surveyState.currentPageIndex === (pageData.survey?.totalPages || 1) - 1}
                  isSubmitting={surveyState.isSubmitting}
                  isSaving={isSaving}
                  hasPendingChanges={hasPendingChanges}
                  primaryColor={primaryColor}
                  onPrevious={previousPage}
                  onNext={nextPage}
                  onSubmit={submitSurvey}
                />
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <p className="text-gray-600">No questions available for this survey.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      {showExitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Are you sure you want to leave?
            </h3>
            <p className="text-gray-600 mb-6">
              Your progress will be saved and you can continue later.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowExitConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmExit}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Leave Survey
              </button>
            </div>
          </div>
        </div>
      )}
    </SurveyContext.Provider>
  );
};